import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';

// ---------------------------------------------------------------------------
// Config loader — reads /config.json injected at deploy time by CDK.
// In local dev the proxy in package.json forwards /query → localhost:3001,
// so we just use a relative URL and skip config.json entirely.
// ---------------------------------------------------------------------------
async function loadConfig() {
  // Local dev: use the proxied relative path
  if (process.env.REACT_APP_API_URL === 'local' || process.env.NODE_ENV === 'development') {
    return { apiUrl: '/query' };
  }
  try {
    const res = await fetch('/config.json');
    if (!res.ok) throw new Error(`config.json returned HTTP ${res.status}`);
    const data = await res.json();
    if (!data.apiUrl) throw new Error('config.json loaded but apiUrl is missing');
    return data;
  } catch (err) {
    console.error('Config load error:', err);
    // Fallback: explicit env var
    const fallback = process.env.REACT_APP_API_URL || '';
    return { apiUrl: fallback, configError: err.message };
  }
}

// ---------------------------------------------------------------------------
// Persona options
// ---------------------------------------------------------------------------
const PERSONAS = [
  { id: 'default', label: '🏌️ Normal Caddie', description: 'Straight answers' },
  { id: 'sarcastic', label: '😏 Sarcastic Golfer', description: 'Dripping with sarcasm' },
  { id: '2nd-grader', label: '🧒 2nd Grader', description: 'Explains like you\'re 7' },
  { id: 'non-reader', label: '🙈 Someone Who Doesn\'t Read', description: 'Barely skimmed it' },
  { id: 'wannabe-pro', label: '🏆 Wannabe Pro', description: 'Thinks they\'re on the PGA Tour' },
];

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------
async function askQuestion(apiUrl, question, persona) {
  const res = await fetch(apiUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ question, persona }),
  });

  const data = await res.json();

  if (!res.ok) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }

  return data; // { answer, sources }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function SourceCard({ source, index }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="source-card" role="listitem">
      <button
        className="source-header"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        aria-controls={`source-body-${index}`}
      >
        <span className="source-index" aria-hidden="true">{index + 1}</span>
        <span className="source-title">{source.title || source.location}</span>
        <span className="source-chevron" aria-hidden="true">
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {expanded && (
        <div id={`source-body-${index}`} className="source-body">
          {source.location && (
            <p className="source-location">
              <strong>Source:</strong>{' '}
              {source.location.startsWith('http') ? (
                <a href={source.location} target="_blank" rel="noopener noreferrer">
                  {source.location}
                </a>
              ) : (
                <code>{source.location}</code>
              )}
            </p>
          )}
          {source.excerpt && (
            <blockquote className="source-excerpt">{source.excerpt}</blockquote>
          )}
        </div>
      )}
    </div>
  );
}

function MessageBubble({ message }) {
  const isUser = message.role === 'user';

  return (
    <div className={`message ${isUser ? 'message--user' : 'message--assistant'}`}>
      <div className="message-avatar" aria-hidden="true">
        {isUser ? '👤' : '⛳'}
      </div>
      <div className="message-content">
        <div className="message-text">{message.text}</div>

        {message.sources && message.sources.length > 0 && (
          <div className="message-sources">
            <p className="sources-label">Sources ({message.sources.length})</p>
            <div role="list" className="sources-list">
              {message.sources.map((src, i) => (
                <SourceCard key={i} source={src} index={i} />
              ))}
            </div>
          </div>
        )}

        {message.error && (
          <div className="message-error" role="alert">
            ⚠️ {message.error}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator() {
  return (
    <div className="message message--assistant" aria-live="polite" aria-label="Assistant is thinking">
      <div className="message-avatar" aria-hidden="true">⛳</div>
      <div className="message-content">
        <div className="typing-indicator">
          <span></span><span></span><span></span>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main App
// ---------------------------------------------------------------------------

export default function App() {
  const [apiUrl, setApiUrl] = useState('');
  const [configLoaded, setConfigLoaded] = useState(false);
  const [configError, setConfigError] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [persona, setPersona] = useState('default');
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Load API URL from config.json on mount
  useEffect(() => {
    loadConfig().then((cfg) => {
      setApiUrl(cfg.apiUrl || '');
      setConfigError(cfg.configError || '');
      setConfigLoaded(true);
    });
  }, []);

  // Auto-scroll to latest message
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      const question = input.trim();
      if (!question || loading) return;

      setInput('');
      setMessages((prev) => [...prev, { role: 'user', text: question }]);
      setLoading(true);

      try {
        if (!apiUrl) throw new Error('API URL not configured. Check config.json.');
        const { answer, sources } = await askQuestion(apiUrl, question, persona);
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', text: answer, sources: sources || [] },
        ]);
      } catch (err) {
        setMessages((prev) => [
          ...prev,
          { role: 'assistant', text: '', error: err.message },
        ]);
      } finally {
        setLoading(false);
        inputRef.current?.focus();
      }
    },
    [apiUrl, input, loading, persona]
  );

  const handleKeyDown = (e) => {
    // Submit on Enter (but allow Shift+Enter for newlines)
    if (e.key === 'Enter' && !e.shiftKey) {
      handleSubmit(e);
    }
  };

  const clearChat = () => {
    setMessages([]);
    inputRef.current?.focus();
  };

  return (
    <div className="app">
      {/* ── Header ── */}
      <header className="app-header" role="banner">
        <div className="header-inner">
          <div className="header-brand">
            <span className="header-icon" aria-hidden="true">⛳</span>
            <div>
              <h1 className="header-title">Caddie AI</h1>
              <p className="header-subtitle">Your AI-powered golf assistant</p>
            </div>
          </div>
          {messages.length > 0 && (
            <button className="btn-clear" onClick={clearChat} aria-label="Clear conversation">
              Clear chat
            </button>
          )}
        </div>
        <div className="header-persona">
          <label htmlFor="persona-select" className="persona-label">Respond as:</label>
          <select
            id="persona-select"
            className="persona-select"
            value={persona}
            onChange={(e) => setPersona(e.target.value)}
            disabled={loading}
          >
            {PERSONAS.map((p) => (
              <option key={p.id} value={p.id}>{p.label}</option>
            ))}
          </select>
        </div>
      </header>

      {/* ── Chat area ── */}
      <main className="chat-area" role="main" aria-label="Conversation">
        {!configLoaded && (
          <div className="empty-state">
            <div className="spinner" aria-label="Loading configuration" />
          </div>
        )}

        {configLoaded && messages.length === 0 && !loading && (
          <div className="empty-state">
            {configError && (
              <div className="config-error" role="alert">
                <strong>⚠️ Configuration error</strong>
                <p>{configError}</p>
                <p className="config-error-hint">
                  The app may not be able to reach the API. Try reloading the page.
                </p>
              </div>
            )}
            <div className="empty-icon" aria-hidden="true">⛳</div>
            <h2 className="empty-title">What's your golf question?</h2>
            <p className="empty-subtitle">
              Ask anything about rules, technique, equipment, courses, or strategy — your caddie has the answers.
            </p>
            <div className="example-questions" role="list" aria-label="Example questions">
              {[
                'How do I fix a slice off the tee?',
                'What club should I use from 150 yards?',
                'What are the rules for a lost ball?',
              ].map((q) => (
                <button
                  key={q}
                  role="listitem"
                  className="example-chip"
                  onClick={() => {
                    setInput(q);
                    inputRef.current?.focus();
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <MessageBubble key={i} message={msg} />
        ))}

        {loading && <TypingIndicator />}

        <div ref={bottomRef} aria-hidden="true" />
      </main>

      {/* ── Input bar ── */}
      <footer className="input-bar" role="contentinfo">
        <form className="input-form" onSubmit={handleSubmit} aria-label="Ask a question">
          <label htmlFor="question-input" className="sr-only">
            Your question
          </label>
          <textarea
            id="question-input"
            ref={inputRef}
            className="input-textarea"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a golf question… (Enter to send, Shift+Enter for new line)"
            rows={1}
            maxLength={2000}
            disabled={loading || !configLoaded}
            aria-label="Question input"
            aria-describedby="char-count"
          />
          <span id="char-count" className="char-count" aria-live="polite">
            {input.length}/2000
          </span>
          <button
            type="submit"
            className="btn-send"
            disabled={!input.trim() || loading || !configLoaded}
            aria-label="Send question"
          >
            {loading ? (
              <span className="spinner-small" aria-hidden="true" />
            ) : (
              <span aria-hidden="true">➤</span>
            )}
          </button>
        </form>
        <p className="input-hint">
          Answers are sourced from the Caddie AI golf knowledge base.
        </p>
      </footer>
    </div>
  );
}
